import React, { useMemo, useState } from "react";
import { RefreshCw, Loader, Download, FileDown, ChevronDown, ChevronRight } from "lucide-react";
import PartnerBalancesUsdSummary from "./PartnerBalancesUsdSummary";
import {
  usePartnerBalancesUsd,
  usePartnerBalances,
  useSyncAllPartnerBalances,
  useSyncOnePartnerBalance,
} from "../hooks/useTreasury";
import { downloadCsv } from "../utils/exportCsv";

// Every provider Treasury currently knows how to sync - kept in one place
// so the manual-pull form doesn't depend on whatever happens to already be
// in partner_balances (a provider that's never synced successfully, e.g. a
// broken integration, still needs to be pullable here).
const KNOWN_PARTNERS = [
  "BRAILS",
  "KORAPAY",
  "QUIDAX",
  "CASHRAMP",
  "BUSHA",
  "EVERSEND",
  "RAFIKI",
  "BLOCKRADAR",
];

// Matches the platform's own supported crypto currencies - rendered at 8
// decimal places (the precision balances are actually persisted at, per
// PartnerBalanceSyncService), not the 2 decimals that suit fiat.
const CRYPTO_CURRENCIES = new Set(["BTC", "ETH", "USDT", "USDC", "SOL", "TRX"]);

const formatBalance = (value, currency) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (isNaN(num)) return "0.00";
  const isCrypto = CRYPTO_CURRENCIES.has(currency?.toUpperCase());
  return num.toLocaleString(undefined, {
    minimumFractionDigits: isCrypto ? 8 : 2,
    maximumFractionDigits: isCrypto ? 8 : 2,
  });
};

function PartnerBalancesTable() {
  // Fetched unfiltered - filtering happens client-side below so the filter
  // dropdowns' own options stay stable rather than shrinking to whatever
  // the current filter already narrowed the server response down to.
  const { data, isLoading, isError, error } = usePartnerBalances();
  const { data: usdSummary, isLoading: usdLoading, isError: usdError } = usePartnerBalancesUsd();
  const { mutate: syncAll, isPending: isSyncingAll } = useSyncAllPartnerBalances();
  const { mutate: syncOne } = useSyncOnePartnerBalance();
  const [syncingPartner, setSyncingPartner] = useState(null);
  const [partnerFilter, setPartnerFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [manualPartner, setManualPartner] = useState(KNOWN_PARTNERS[0]);
  const [manualCurrency, setManualCurrency] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  // The API returns every historical snapshot, most recent first - keep
  // only the latest row per (partner, currency) for display. An error row
  // (source='error', currency null) has no currency to key on, so it's
  // tracked per-partner instead - it's a status, not a balance.
  const latestRows = useMemo(() => {
    const rows = data ?? [];
    const seen = new Map();
    for (const row of rows) {
      const key = row.currency ? `${row.partner}:${row.currency}` : `${row.partner}:__status__`;
      if (!seen.has(key)) seen.set(key, row);
    }
    return Array.from(seen.values());
  }, [data]);

  const partnerOptions = useMemo(
    () => Array.from(new Set(latestRows.map((r) => r.partner))).sort(),
    [latestRows],
  );
  const currencyOptions = useMemo(
    () =>
      Array.from(new Set(latestRows.map((r) => r.currency).filter(Boolean))).sort(),
    [latestRows],
  );

  const filteredRows = useMemo(() => {
    return latestRows.filter((row) => {
      if (partnerFilter && row.partner !== partnerFilter) return false;
      if (currencyFilter && row.currency !== currencyFilter) return false;
      return true;
    });
  }, [latestRows, partnerFilter, currencyFilter]);

  // USD value per partner and currency, from the summary endpoint.
  const usdByKey = useMemo(() => {
    const map = new Map();
    for (const r of usdSummary?.rows ?? []) map.set(`${r.partner}:${r.currency}`, r.usd);
    return map;
  }, [usdSummary]);
  const usdFor = (row) => (row.currency ? usdByKey.get(`${String(row.partner).toUpperCase()}:${String(row.currency).toUpperCase()}`) : undefined);
  const filteredUsdTotal = useMemo(
    () => filteredRows.reduce((sum, row) => sum + (Number(usdFor(row)) || 0), 0),
    [filteredRows, usdByKey],
  );

  const handleSyncOne = (partner) => {
    setSyncingPartner(partner);
    syncOne({ partner }, { onSettled: () => setSyncingPartner(null) });
  };

  // Exports what is on screen (the latest balance per partner and currency,
  // with the filters applied). Amounts are plain numbers, not locale text.
  const handleExport = () => {
    const headers = ["Partner", "Currency", "Balance", "Balance (USD)", "Available", "Pending", "Reserve", "Status", "Error", "Captured at"];
    const rows = filteredRows.map((row) => {
      const c = row.metadata?.components;
      return [
        row.partner,
        row.currency ?? "",
        row.source === "error" ? "" : Number(row.balance),
        usdFor(row) == null ? "" : Number(usdFor(row)),
        c ? Number(c.available) : "",
        c ? Number(c.pending) : "",
        c ? Number(c.reserve) : "",
        row.source,
        row.error ?? "",
        row.capturedAt ?? "",
      ];
    });
    downloadCsv(`partner-balances-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  };

  const handleManualPull = (e) => {
    e.preventDefault();
    const currency = manualCurrency.trim().toUpperCase();
    if (!currency) return;
    setSyncingPartner(manualPartner);
    syncOne(
      { partner: manualPartner, currency },
      { onSettled: () => setSyncingPartner(null) },
    );
  };

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error.message}
      </p>
    );
  }

  return (
    <div>
      <form
        onSubmit={handleManualPull}
        className="flex flex-wrap gap-3 items-end pb-4 mb-4 border-b border-gray-100"
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Pull one currency</label>
          <div className="flex gap-2">
            <select
              value={manualPartner}
              onChange={(e) => setManualPartner(e.target.value)}
              className="px-3 py-2 text-sm capitalize rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              {KNOWN_PARTNERS.map((p) => (
                <option key={p} value={p} className="capitalize">
                  {p.toLowerCase()}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={manualCurrency}
              onChange={(e) => setManualCurrency(e.target.value)}
              placeholder="e.g. NGN"
              maxLength={10}
              className="px-3 py-2 w-28 text-sm uppercase rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={syncingPartner === manualPartner || !manualCurrency.trim()}
          className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-[#013941] bg-white rounded-3xl border border-[#013941] transition-all hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className={`w-4 h-4 ${syncingPartner === manualPartner ? "animate-spin" : ""}`} />
          Pull
        </button>
        <p className="text-xs text-gray-400">
          Fetches everything from this provider, but only saves this one currency - useful to check whether a specific balance is actually being returned.
        </p>
      </form>

      <div className="flex flex-col gap-3 justify-between items-start pb-4 md:flex-row md:items-center">
        <div className="flex flex-wrap gap-3">
          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="px-3 py-2 text-sm capitalize rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="">All providers</option>
            {partnerOptions.map((p) => (
              <option key={p} value={p} className="capitalize">
                {p.toLowerCase()}
              </option>
            ))}
          </select>

          <select
            value={currencyFilter}
            onChange={(e) => setCurrencyFilter(e.target.value)}
            className="px-3 py-2 text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="">All currencies</option>
            {currencyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={handleExport}
            disabled={isLoading || filteredRows.length === 0}
            title="Download the balances shown (with the filters applied) as a CSV file"
            className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-[#013941] bg-white rounded-3xl border border-[#013941] transition-all hover:bg-gray-50 disabled:opacity-50"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => syncAll()}
            disabled={isSyncingAll}
            className="flex gap-2 items-center px-4 py-2 text-sm font-bold text-white bg-[#013941] rounded-3xl transition-all hover:bg-[#012126] disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncingAll ? "animate-spin" : ""}`} />
            Refresh all
          </button>
        </div>
      </div>

      <PartnerBalancesUsdSummary
        summary={usdSummary}
        isLoading={usdLoading}
        isError={usdError}
        filtered={Boolean(partnerFilter || currencyFilter)}
        filteredTotal={filteredUsdTotal}
      />

      {isLoading ? (
        <div className="flex h-[300px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading partner balances <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="w-8 border-b"></th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Partner</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Currency</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Balance</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Balance (USD)</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Status</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Last Attempt</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.length > 0 ? (
                  filteredRows.map((row) => {
                    const isError = row.source === "error";
                    const wallets = row.metadata?.wallets;
                    const components = row.metadata?.components;
                    const extraHeld = components ? Number(components.pending || 0) + Number(components.reserve || 0) : 0;
                    const hasComponents = extraHeld > 0;
                    const hasBreakdown = (Array.isArray(wallets) && wallets.length > 0) || hasComponents;
                    const rowKey = `${row.partner}-${row.currency ?? "status"}`;
                    const isExpanded = expandedRow === rowKey;
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className={`transition-colors hover:bg-gray-50 ${hasBreakdown ? "cursor-pointer" : ""}`}
                          onClick={() => hasBreakdown && setExpandedRow(isExpanded ? null : rowKey)}
                        >
                          <td className="px-2 text-center text-gray-400">
                            {hasBreakdown && (isExpanded ? <ChevronDown className="inline w-4 h-4" /> : <ChevronRight className="inline w-4 h-4" />)}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm font-medium text-gray-900 capitalize md:px-6 md:py-5">
                            {row.partner?.toLowerCase()}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5">
                            {row.currency ?? "—"}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                            {formatBalance(row.balance, row.currency)}
                            {hasComponents && (
                              <span
                                className="block mt-0.5 text-xs font-medium text-amber-700"
                                title="Total includes pending and reserve balances, which are not immediately spendable"
                              >
                                incl. {formatBalance(extraHeld, row.currency)} pending/reserve
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                            {isError || usdFor(row) === undefined
                              ? "—"
                              : usdFor(row) === null
                                ? <span className="text-xs text-red-600" title={usdSummary?.unpricedReasons?.[String(row.currency).toUpperCase()] || "No rate available"}>no rate</span>
                                : Number(usdFor(row)).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm md:px-6 md:py-5">
                            {isError ? (
                              <span
                                className="px-3 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full"
                                title={row.error}
                              >
                                failed: {row.error?.slice(0, 40)}
                              </span>
                            ) : (
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${row.source === "api" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"
                                  }`}
                              >
                                {row.source}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm text-gray-600 whitespace-nowrap md:px-6 md:py-5">
                            {row.capturedAt ? new Date(row.capturedAt).toLocaleString() : "N/A"}
                          </td>
                          <td className="px-4 py-3 font-sans text-sm md:px-6 md:py-5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSyncOne(row.partner);
                              }}
                              disabled={syncingPartner === row.partner}
                              className="flex gap-1 items-center text-[#013941] hover:underline disabled:opacity-50"
                            >
                              <RefreshCw
                                className={`w-3.5 h-3.5 ${syncingPartner === row.partner ? "animate-spin" : ""}`}
                              />
                              Refresh
                            </button>
                          </td>
                        </tr>
                        {isExpanded && hasBreakdown && (
                          <tr className="bg-gray-50/70">
                            <td></td>
                            <td colSpan={7} className="px-6 py-4">
                              {hasComponents && (
                                <div className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
                                  <div className="text-xs font-bold tracking-wider text-amber-800 uppercase">
                                    Total = available + pending + reserve
                                  </div>
                                  <div className="flex flex-wrap gap-6 mt-2 text-sm">
                                    <div><span className="text-gray-500">Available: </span><span className="font-medium text-gray-900">{formatBalance(components.available, row.currency)}</span></div>
                                    <div><span className="text-amber-800">Pending: </span><span className="font-semibold text-amber-900">{formatBalance(components.pending, row.currency)}</span></div>
                                    <div><span className="text-amber-800">Reserve: </span><span className="font-semibold text-amber-900">{formatBalance(components.reserve, row.currency)}</span></div>
                                  </div>
                                </div>
                              )}
                              {Array.isArray(wallets) && wallets.length > 0 && (
                              <>
                              <div className="text-xs font-bold tracking-wider text-gray-500 uppercase">
                                Pulled from {wallets.length} wallet{wallets.length > 1 ? "s" : ""}
                              </div>
                              <div className="flex flex-wrap gap-6 mt-2">
                                {wallets.map((w) => (
                                  <div key={w.walletId} className="text-sm">
                                    <span className="text-gray-500">
                                      {w.walletName || w.walletId}
                                      {w.network ? ` (${w.network})` : ""}:{" "}
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {formatBalance(w.balance, row.currency)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              </>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {latestRows.length === 0
                        ? 'No partner balances recorded yet - click "Refresh all" to sync.'
                        : "No rows match the current filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerBalancesTable;
